import { useState } from 'react'
import { Plus, Terminal, Copy, Check } from 'lucide-react'
import { Header } from '@/components/layout/Header'
import { Button } from '@/components/ui/button'
import { NodeCard } from '@/components/nodes/NodeCard'
import { useNodes, useDeleteNode } from '@/hooks/useNodes'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

function CodeBlock({ code, className }: { code: string; className?: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className={`group relative ${className ?? ''}`}>
      <pre className="overflow-x-auto whitespace-pre-wrap break-all rounded-md bg-muted p-3 pr-10 text-xs font-mono">
        <code>{code}</code>
      </pre>
      <Button
        variant="ghost"
        size="icon"
        className="absolute right-1 top-1 h-7 w-7"
        onClick={handleCopy}
      >
        {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5 text-muted-foreground" />}
      </Button>
    </div>
  )
}

export function Nodes() {
  const { data: nodes, isLoading, error } = useNodes()
  const deleteNode = useDeleteNode()
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [showAddDialog, setShowAddDialog] = useState(false)


  const handleDelete = () => {
    if (deleteId) {
      deleteNode.mutate(deleteId, {
        onSuccess: () => setDeleteId(null),
      })
    }
  }

  if (error) {
    return (
      <div className="p-6">
        <Header title="Nodes" />
        <div className="mt-6 rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-destructive">Failed to load nodes: {error.message}</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <Header title="Nodes" description="Manage your proxy server nodes">
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Node
        </Button>
      </Header>

      <div className="p-6">
        {isLoading ? (
          <div className="text-muted-foreground">Loading nodes...</div>
        ) : nodes?.length === 0 ? (
          <div className="rounded-lg border border-dashed p-12 text-center">
            <h3 className="text-lg font-semibold">No nodes configured</h3>
            <p className="mt-2 text-muted-foreground">
              Get started by adding your first proxy node.
            </p>
            <Button className="mt-4" onClick={() => setShowAddDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Node
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {nodes?.map((node) => (
              <NodeCard
                key={node.id}
                node={node}
                onDelete={() => setDeleteId(node.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Node</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this node? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteNode.isPending}
            >
              {deleteNode.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Node Dialog - Shows CLI Instructions */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Terminal className="h-5 w-5" />
              Deploy a New Node
            </DialogTitle>
            <DialogDescription>
              Nodes are deployed via CLI. Run these commands in your terminal.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <h4 className="mb-2 text-sm font-medium">1. Login (if not already)</h4>
              <CodeBlock code="fkgfw-deploy login" />
            </div>

            <div>
              <h4 className="mb-2 text-sm font-medium">2. Deploy node</h4>
              <CodeBlock code="fkgfw-deploy terraform deploy \
  --provider gcp \
  --name taiwan-1 \
  --region asia-east1" />
            </div>

            <div>
              <h4 className="mb-2 text-sm font-medium">Providers & Regions</h4>
              <div className="space-y-1 text-xs text-muted-foreground">
                <p><span className="font-mono text-foreground">gcp</span> - asia-east1 (Taiwan), asia-northeast1 (Tokyo)</p>
                <p><span className="font-mono text-foreground">aws</span> - ap-northeast-1 (Tokyo), ap-southeast-1 (Singapore)</p>
                <p><span className="font-mono text-foreground">oracle</span> - ap-tokyo-1, ap-osaka-1 (Free tier)</p>
              </div>
            </div>

            <div className="rounded-md border border-blue-500/20 bg-blue-500/10 p-3 text-xs">
              <p className="text-blue-400">
                CLI will provision, deploy via Terraform, cross-compile, and install automatically.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
